import { createContext, useContext, useState } from 'react';

const CartContext = createContext();

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);

  const addToCart = (product, quantity) => {
    setCart(prevCart => {
      // For clothes, check size and color variations
      if (product.selectedSize && product.selectedColor) {
        const existingItemIndex = prevCart.findIndex(item => 
          item.id === product.id && 
          item.selectedSize === product.selectedSize && 
          item.selectedColor === product.selectedColor
        );

        if (existingItemIndex >= 0) {
          const updatedCart = [...prevCart];
          updatedCart[existingItemIndex].quantity += quantity;
          return updatedCart;
        }

        // Add new variation for clothes
        return [...prevCart, { 
          ...product,
          variationId: `${product.id}-${product.selectedSize}-${product.selectedColor}`,
          quantity 
        }];
      }

      // For groceries and jewelry (no variations)
      const existingItemIndex = prevCart.findIndex(item => item.id === product.id);

      if (existingItemIndex >= 0) {
        const updatedCart = [...prevCart];
        updatedCart[existingItemIndex].quantity += quantity;
        return updatedCart;
      }

      // Add new item for groceries/jewelry
      return [...prevCart, { ...product, quantity }];
    });
  };

  const removeFromCart = (itemId, size, color) => {
    setCart(prevCart => {
      // For clothes with variations
      if (size && color) {
        return prevCart.filter(item => 
          !(item.id === itemId && 
            item.selectedSize === size && 
            item.selectedColor === color)
        );
      }
      // For groceries and jewelry
      return prevCart.filter(item => item.id !== itemId);
    });
  };

  const updateQuantity = (itemId, newQuantity, size, color) => {
    setCart(prevCart => {
      // Remove if quantity is less than 1
      if (newQuantity < 1) {
        return removeFromCart(itemId, size, color);
      }

      return prevCart.map(item => {
        // For clothes with variations
        if (size && color) {
          if (item.id === itemId && 
              item.selectedSize === size && 
              item.selectedColor === color) {
            return { ...item, quantity: newQuantity };
          }
          return item;
        }
        
        // For groceries and jewelry
        if (item.id === itemId) {
          return { ...item, quantity: newQuantity };
        }
        return item;
      });
    });
  };

  const clearCart = () => {
    setCart([]);
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartItemCount = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  return (
    <CartContext.Provider value={{
      cart,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      getCartTotal,
      getCartItemCount
    }}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
